import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box,
  Typography, 
  CircularProgress, 
  IconButton,
  Tooltip, 
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import DataGridComponent from 'components/DataGridComponent';
import DeleteConfirm from 'components/DeleteConfirm';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import CardList from 'components/CardList';


const Products = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "Products", "create", false);
  const canEdit = hasRight(permissionMap, "Products", "update", false);
  const canDelete = hasRight(permissionMap, "Products", "delete", false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  useEffect(() => {
    const handleProducts = async () => {
      try {
        setLoading(true);
        const res = await api.get('/products');
        setProducts(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    handleProducts();
  }, []);

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, products);
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        await api.delete(`/products/delete/${idToDelete}`);
        setProducts(products.filter((item) => item._id !== idToDelete));
        toast.success('Product deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, products);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }
        const res = await api.patch('/products/soft-delete', { ids: selectedIds });
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        setProducts(products.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete products!");
    }
  }, [canDelete, deleteType, idToDelete, rowSelectionModel, products]);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      width: 60,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "productCode",
      headerName: "Code",
      width: 80,
    },
    {
      field: "productName",
      headerName: "Product Name",
      width: 300,
    },
    {
      field: "description",
      headerName: "Description",
      width: 200,
    },
    {
      field: "unit",
      headerName: "Unit",
      width: 80,
    },
    {
      field: "price",
      headerName: "Price",
      align: 'right',
      width: 90,
      valueFormatter: (params) => params?.toLocaleString(),
    },
    {
      field: "domesticPrice",
      headerName: "DomPrice",
      align: 'right',
      width: 90,
      valueFormatter: (params) => params?.toLocaleString(),
    },
    {
      field: "internationalPrice",
      headerName: "IntPrice",
      align: 'right',
      width: 90,
      valueFormatter: (params) => params?.toLocaleString(),
    },
    {
      field: "preorderCommissionRate",
      headerName: "PreorderCommissionRate",
      align: 'right',
      width: 120,
      valueFormatter: (params) => params?.toLocaleString(),
    },
    {
      field: "directCommissionRate",
      headerName: "DirectCommissionRate",
      align: 'right',
      width: 120,
      valueFormatter: (params) => params?.toLocaleString(),
    },
    {
      field: "category",
      headerName: "Category",
      width: 100,
    },
    {
      field: "sortOrder",
      headerName: "SortOrder",
      width: 80,
    },
    {
      field: 'suppliers',
      headerName: 'Suppliers/ DomPrice/ IntPrice',
      width: 200,
      renderCell: (params) => (
        <Box
          sx={{
            height: '100%',
            alignItems: 'flex-start',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {params.row?.suppliers?.map((supplier, index) => (
            <FlexBetween key={index} gap={2}>
              <Typography variant="body2">{supplier.supplierName}</Typography>
              <Typography variant="body2">{supplier.domesticPurchasePrice}</Typography>
              <Typography variant="body2">{supplier.internationalPurchasePrice}</Typography>
            </FlexBetween>
          ))}
        </Box>
      ),
    },
    {
      field: "isPreorderHidden",
      headerName: "IsPreorderHidden",
      width: 120,
    },
    {
      field: "isDirectHidden",
      headerName: "IsDirectHidden",
      width: 120,
    },
    {
      field: "isActive",
      headerName: "IsActive",
      width: 100,
    },
  ], []);

  const actionColumn = useMemo(() => {
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => (
          <FlexCenter gap={1} sx={{ width: '100%', height: '100%' }}>
            {canEdit && (
              <Tooltip title="Edit">
                <IconButton
                  aria-label="Edit"
                  sx={{
                    color: theme.palette.primary.dark,
                    '&:hover': { color: theme.palette.green.deep },
                  }}
                  onClick={() => { if (!canEdit) return; navigate(`/products/${params.row._id}`); }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  aria-label="Delete"
                  sx={{
                    color: theme.palette.primary.dark,
                    '&:hover': { color: theme.palette.green.deep },
                  }}
                  onClick={() => { if (!canDelete) return; handleDeleteClick(params.row._id); }}
                >
                  <DeleteForever />
                </IconButton>
              </Tooltip>
            )}
          </FlexCenter>
        ),
      },
    ];
  }, [navigate, theme.palette, canEdit, canDelete]);

  const columnsToShow = [...columns, ...actionColumn];

  const gridStyles = {
    "& .MuiDataGrid-row.isActive-false": {
      backgroundColor: `${theme.palette.primary.light} !important`,
    },
  };


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Products"
          subtitle={`${loading ? 'Loading...' : 'List of Products'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, products)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/products/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box height={LAYOUT_HEIGHT} sx={{ gridColumn: "span 12" }}>
            {isMobile ? (
              <CardList
                rows={products}
                primaryField="productName"
                fields={[
                  { label: "Code", key: "productCode" },
                  { label: "Description", key: "description" },
                  { label: "Category", key: "category" },
                  { label: "Price", key: "price" },
                  { label: "Domestic Price", key: "domesticPrice" },
                  { label: "International Price", key: "internationalPrice" },
                  { label: "Direct Commission Rate", key: "directCommissionRate" },
                  { label: "Preorder Commission Rate", key: "preorderCommissionRate" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/products/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={products || []}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  onRowSelectionModelChange: setRowSelectionModel,
                  rowSelectionModel,
                  getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                  sx: gridStyles,
                  initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                }}
              />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default Products;
